import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router";
import { useMe, useServices } from "../app/services";
import { setMotionChoice, systemReduces, useMotionChoice, useReducedMotion } from "../motion/preference";
import { Icon, type IconName } from "./Icon";

export const NAV_ITEMS: Array<{ to: string; label: string; icon: IconName; end?: boolean }> = [
  { to: "/", label: "見つける", icon: "compass", end: true },
  { to: "/now", label: "いま聴く", icon: "wave" },
  { to: "/history", label: "履歴", icon: "history" },
  { to: "/library", label: "ライブラリ", icon: "library" },
];

/** 画面を移ったときに、いちばん上を横切る3色の細い帯（motion.css の route-arrive。参照元のページ移動の帯を、画面をふさがない形に） */
export function RouteSweep() {
  return (
    <div className="route-sweep" aria-hidden="true">
      <span className="route-sweep__band route-sweep__band--cyan" />
      <span className="route-sweep__band route-sweep__band--lavender" />
      <span className="route-sweep__band route-sweep__band--petal" />
    </div>
  );
}

/**
 * 共通ヘッダーのDiscordアイコンから開くアカウントと設定。
 * 開き方は参照元の nav-menu（ボタンの角からふわっと開き、閉じるときは逆の順に消える。Esc でも閉じる）
 */
export function AccountMenu() {
  const me = useMe();
  const { api, session } = useServices();
  const choice = useMotionChoice();
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDetailsElement>(null);
  const location = useLocation();
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const finishClose = () => {
    const d = ref.current;
    if (!d) return;
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
    delete d.dataset.closing;
    d.open = false;
  };
  const startClose = () => {
    const d = ref.current;
    if (!d || !d.open) return;
    if (reduced) return finishClose();
    d.dataset.closing = "true";
    closeTimer.current = setTimeout(finishClose, 450);
  };

  // 画面を移ったら閉じる
  useEffect(() => {
    finishClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !ref.current?.open) return;
      startClose();
      ref.current.querySelector<HTMLElement>(":scope > summary")?.focus();
    };
    const onDocClick = (e: MouseEvent) => {
      if (ref.current?.open && !ref.current.contains(e.target as Node)) startClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("click", onDocClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("click", onDocClick);
    };
  });

  const logout = async () => {
    try {
      await api.logout();
    } finally {
      await session.load();
    }
  };

  return (
    <details className="nav-menu" ref={ref}>
      <summary
        onClick={(e) => {
          const d = ref.current;
          if (d?.open) {
            e.preventDefault();
            if (d.dataset.closing) {
              // 閉じている途中でもう一度押したら、開いたままにする
              if (closeTimer.current) clearTimeout(closeTimer.current);
              delete d.dataset.closing;
            } else startClose();
          }
        }}
        aria-label="アカウントと設定"
      >
        <AccountAvatar key={`${me?.id}:${me?.avatar_url}`} url={me?.avatar_url} name={me?.display_name ?? "メニュー"} />
        <span className="nav-menu__name">{me ? me.display_name : "メニュー"}</span>
      </summary>
      <div
        className="nav-menu__panel"
        onAnimationEnd={(e) => {
          if (e.animationName === "menu-close") finishClose();
        }}
      >
        {me && (
          <p className="nav-menu__who">
            <strong>{me.display_name}</strong>
            <span>
              {me.voice_channel_id ? (me.voice_channel_name ? `#${me.voice_channel_name} に参加中` : "VCに参加中") : "VCに参加していません"}
            </span>
          </p>
        )}
        <ul>
          <li><Link to="/guide"><Icon name="compass" />ほろりばレコードの使い方</Link></li>
          {me && <li><Link to="/posts"><Icon name="sparkle" />みんなの投稿</Link></li>}
          {me && (
            <>
              <li>
                <Link to="/search">
                  <Icon name="search" />
                  曲・歌った人を探す
                </Link>
              </li>
            </>
          )}
          {me?.can_moderate && (
            <li>
              <Link to="/moderation">
                <Icon name="flag" />
                通報の確認（管理者）
              </Link>
            </li>
          )}
          <li className="nav-menu__setting">
            <label className="switch">
              <input
                type="checkbox"
                role="switch"
                checked={reduced}
                disabled={systemReduces()}
                aria-describedby="motion-note"
                onChange={(e) => setMotionChoice(e.target.checked ? "reduce" : "system")}
              />
              <span className="switch__track" aria-hidden="true" />
              <span>動きを減らす</span>
            </label>
            <small id="motion-note">
              {systemReduces()
                ? "端末の設定で動きを減らしています。"
                : choice === "reduce"
                  ? "この端末で動きを減らしています。"
                  : "端末の設定に合わせています。"}
            </small>
          </li>
        </ul>
        {me && (
          <button
            type="button"
            className="btn btn-secondary btn-small nav-menu__logout"
            onClick={logout}
            disabled={!api.isConnected("logout")}
          >
            ログアウト
          </button>
        )}
      </div>
    </details>
  );
}

export function AccountAvatar({ url, name }: { url?: string | null; name: string }) {
  const [failed, setFailed] = useState(false);
  return url && !failed
    ? <img className="nav-menu__avatar" src={url} alt="" width={32} height={32}
        referrerPolicy="no-referrer" onError={() => setFailed(true)} />
    : <span className="nav-menu__avatar nav-menu__avatar--fallback" aria-hidden="true">{Array.from(name.trim())[0] || "?"}</span>;
}

/** 主なメニュー。どの幅でも、下に浮かぶ丸いすりガラスの帯にまとめる */
export function BottomNav() {
  return (
    <nav className="bottom-nav" aria-label="主なメニュー">
      <ul>
        {NAV_ITEMS.map((item) => (
          <li key={item.to}>
            <NavLink to={item.to} end={item.end} className="bottom-nav__link">
              <Icon name={item.icon} className="bottom-nav__icon" />
              <span>{item.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
